import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeOff, CheckCircle, XCircle, Lock } from 'lucide-react';
import { LocaleText } from '../i18n/Locale';
import { LanguageSwitch } from '../i18n/LanguageSwitch';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function PasswordReset() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  
  const [loading, setLoading] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [success, setSuccess] = useState(false);
  
  const [formData, setFormData] = useState({
    newPassword: '',
    confirmPassword: ''
  });
  
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState('');
  
  // Password validation rules
  const validatePassword = (password) => {
    const minLength = password.length >= 8;
    const hasUpper = /[A-Z]/.test(password);
    const hasLower = /[a-z]/.test(password);
    const hasNumber = /\d/.test(password);
    const hasSpecial = /[!@#$%^&*(),.?":{}|<>]/.test(password);
    
    return {
      minLength,
      hasUpper,
      hasLower,
      hasNumber,
      hasSpecial,
      isValid: minLength && hasUpper && hasLower && hasNumber && hasSpecial
    };
  };
  
  const passwordValidation = validatePassword(formData.newPassword);
  
  // Verify token on component mount
  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setLoading(false);
        setMessage('Invalid or missing password reset token.');
        return;
      }
      
      try {
        const response = await axios.get(`${API}/auth/verify-reset-token?token=${encodeURIComponent(token)}`);
        
        if (response.data.valid) {
          setTokenValid(true);
          setUserEmail(response.data.email);
        } else {
          setMessage(response.data.message || 'Invalid or expired password reset token.');
        }
      } catch (error) {
        console.error('Token verification error:', error);
        setMessage('Invalid or expired password reset token.');
      } finally {
        setLoading(false);
      }
    };
    
    verifyToken();
  }, [token]);
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});
    setMessage('');
    
    // Validate passwords
    if (!passwordValidation.isValid) {
      setErrors({ newPassword: 'Password does not meet requirements' });
      return;
    }
    
    if (formData.newPassword !== formData.confirmPassword) {
      setErrors({ confirmPassword: 'Passwords do not match' });
      return;
    }
    
    setResetting(true);
    
    try {
      const response = await axios.post(`${API}/auth/reset-password`, {
        token,
        new_password: formData.newPassword,
        confirm_password: formData.confirmPassword
      });
      
      setSuccess(true);
      setMessage(response.data.message);
      
      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate('/');
      }, 3000);
      
    } catch (error) {
      console.error('Password reset error:', error);
      setMessage(error.response?.data?.detail || 'An error occurred. Please try again.');
    } finally {
      setResetting(false);
    }
  };
  
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="text-center">
          <Lock className="w-12 h-12 text-blue-400 mx-auto mb-4" />
          <p className="text-slate-300" data-testid="password-reset-loading">Verifying reset token...</p>
        </div>
      </div>
    );
  }
  
  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-slate-800 rounded-lg shadow-xl p-8 text-center">
            <XCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-100 mb-4">Invalid Reset Link</h1>
            <p className="text-slate-300 mb-6" data-testid="password-reset-invalid-message"><LocaleText value={message} /></p>
            <Button
              data-testid="password-reset-invalid-back"
              onClick={() => navigate('/')}
              className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white border-0"
            >
              Return to Login
            </Button>
          </div>
        </div>
      </div>
    );
  }
  
  if (success) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="max-w-md w-full mx-4">
          <div className="bg-slate-800 rounded-lg shadow-xl p-8 text-center">
            <CheckCircle className="w-16 h-16 text-green-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-100 mb-4">Password Reset Successful</h1>
            <p className="text-slate-300 mb-6" data-testid="password-reset-success-message"><LocaleText value={message} /></p>
            <p className="text-sm text-slate-400">Redirecting to login page...</p>
          </div>
        </div>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center">
      <div className="max-w-md w-full mx-4">
        <div className="bg-slate-800 rounded-lg shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <Lock className="w-12 h-12 text-blue-400 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-slate-100 mb-2">Reset Your Password</h1>
            <p className="text-slate-400">Creating new password for: <span translate="no" data-testid="password-reset-email">{userEmail}</span></p>
          </div>
          
          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-6" data-testid="password-reset-form">
            {/* New Password Field */}
            <div className="space-y-2">
              <Label htmlFor="newPassword" className="text-slate-300">New Password</Label>
              <div className="relative">
                <Input
                  id="newPassword"
                  data-testid="password-reset-new-password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Create new password"
                  value={formData.newPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, newPassword: e.target.value }))}
                  className={`bg-slate-700 border-slate-600 text-slate-100 placeholder:text-slate-400 pr-10 ${
                    errors.newPassword ? 'border-red-500' : ''
                  }`}
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="password-reset-toggle-new"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                >
                  {showPassword ? <EyeOff className="w-4 h-4 text-slate-400" /> : <Eye className="w-4 h-4 text-slate-400" />}
                </Button>
              </div>
              
              {/* Password Requirements */}
              {formData.newPassword && (
                <div className="bg-slate-700/50 p-3 rounded text-sm">
                  <p className="text-slate-300 mb-2 font-medium">Password Requirements:</p>
                  <div className="grid grid-cols-1 gap-1">
                    <div className={`flex items-center gap-2 ${passwordValidation.minLength ? 'text-green-400' : 'text-slate-400'}`}>
                      <span className="text-xs">{passwordValidation.minLength ? '✓' : '○'}</span>
                      <span>At least 8 characters</span>
                    </div>
                    <div className={`flex items-center gap-2 ${passwordValidation.hasUpper ? 'text-green-400' : 'text-slate-400'}`}>
                      <span className="text-xs">{passwordValidation.hasUpper ? '✓' : '○'}</span>
                      <span>One uppercase letter</span>
                    </div>
                    <div className={`flex items-center gap-2 ${passwordValidation.hasLower ? 'text-green-400' : 'text-slate-400'}`}>
                      <span className="text-xs">{passwordValidation.hasLower ? '✓' : '○'}</span>
                      <span>One lowercase letter</span>
                    </div>
                    <div className={`flex items-center gap-2 ${passwordValidation.hasNumber ? 'text-green-400' : 'text-slate-400'}`}>
                      <span className="text-xs">{passwordValidation.hasNumber ? '✓' : '○'}</span>
                      <span>One number</span>
                    </div>
                    <div className={`flex items-center gap-2 ${passwordValidation.hasSpecial ? 'text-green-400' : 'text-slate-400'}`}>
                      <span className="text-xs">{passwordValidation.hasSpecial ? '✓' : '○'}</span>
                      <span>One special character</span>
                    </div>
                  </div>
                </div>
              )}
              
              {errors.newPassword && (
                <p className="text-red-400 text-sm" data-testid="password-reset-new-error"><LocaleText value={errors.newPassword} /></p>
              )}
            </div>
            
            {/* Confirm Password Field */}
            <div className="space-y-2">
              <Label htmlFor="confirmPassword" className="text-slate-300">Confirm New Password</Label>
              <div className="relative">
                <Input
                  id="confirmPassword"
                  data-testid="password-reset-confirm-password"
                  type={showConfirmPassword ? "text" : "password"}
                  placeholder="Confirm new password"
                  value={formData.confirmPassword}
                  onChange={(e) => setFormData(prev => ({ ...prev, confirmPassword: e.target.value }))}
                  className={`bg-slate-700 border-slate-600 text-slate-100 placeholder:text-slate-400 pr-10 ${
                    errors.confirmPassword ? 'border-red-500' : ''
                  }`}
                  required
                />
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  data-testid="password-reset-toggle-confirm"
                  aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                >
                  {showConfirmPassword ? <EyeOff className="w-4 h-4 text-slate-400" /> : <Eye className="w-4 h-4 text-slate-400" />}
                </Button>
              </div>
              
              {/* Password Match Indicator */}
              {formData.confirmPassword && (
                <div data-testid="password-reset-match-status" className={`text-sm ${
                  formData.newPassword === formData.confirmPassword 
                    ? 'text-green-400' 
                    : 'text-red-400'
                }`}>
                  {formData.newPassword === formData.confirmPassword 
                    ? '✓ Passwords match' 
                    : '✗ Passwords do not match'
                  }
                </div>
              )}
              
              {errors.confirmPassword && (
                <p className="text-red-400 text-sm" data-testid="password-reset-confirm-error"><LocaleText value={errors.confirmPassword} /></p>
              )}
            </div>
            
            {/* Error Message */}
            {message && !success && (
              <div data-testid="password-reset-error" role="alert" className="bg-red-900/50 border border-red-600 text-red-300 text-sm p-3 rounded">
                <LocaleText value={message} />
              </div>
            )}
            
            {/* Submit Button */}
            <Button
              type="submit"
              disabled={resetting || !passwordValidation.isValid || formData.newPassword !== formData.confirmPassword}
              data-testid="password-reset-submit"
              className="w-full bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white border-0"
            >
              {resetting ? 'Resetting Password...' : 'Reset Password'}
            </Button>
            
            {/* Back to Login Link */}
            <div className="text-center">
              <button
                type="button"
                onClick={() => navigate('/')}
                data-testid="password-reset-back"
                className="text-sm text-blue-400 hover:text-blue-300 underline"
              >
                Back to Login
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

export default function LocalizedPasswordReset() {
  return <><div className="standalone-language"><LanguageSwitch id="password-reset-language-switch" /></div><PasswordReset /></>;
}